import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchRestaurantJreatsSchools,
  mapRestaurantJreatsSchool,
  searchRestaurantJreatsSchools,
  unmapRestaurantJreatsSchool,
} from './restaurantApi';
import './css/AgePricingEditor.css';

function orgIdOf(row) {
  return String(row?.organizationId ?? row?.organization_id ?? '').trim();
}

function orgNameOf(row) {
  return String(row?.organizationName ?? row?.organization_name ?? '').trim() || `School ${orgIdOf(row)}`;
}

/**
 * Map Schools for Jr Eats — restaurant-scoped (auto hub), same idea as Map Offices.
 */
export default function JrEatsMapSchoolsPanel({
  restaurantId,
  userId,
  disabled = false,
}) {
  const [mapped, setMapped] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [name, setName] = useState('');
  const [radiusKm, setRadiusKm] = useState('10');
  const [message, setMessage] = useState('');

  const mappedIds = useMemo(
    () => new Set(mapped.map((row) => orgIdOf(row)).filter(Boolean)),
    [mapped],
  );

  const loadMapped = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setMessage('');
    try {
      const schools = await fetchRestaurantJreatsSchools(restaurantId);
      setMapped(schools);
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to load mapped schools.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  const searchNearby = useCallback(async () => {
    if (!restaurantId) return;
    setSearching(true);
    setMessage('');
    try {
      const schools = await searchRestaurantJreatsSchools(restaurantId, {
        name: name.trim(),
        radiusKm: Number(radiusKm) || 10,
      });
      setNearby(schools);
      if (!schools.length) {
        setMessage(`No schools found within ${radiusKm || 10} km.`);
      }
    } catch (error) {
      setNearby([]);
      setMessage(error?.response?.data?.error || error?.message || 'Failed to search schools.');
    } finally {
      setSearching(false);
    }
  }, [name, radiusKm, restaurantId]);

  useEffect(() => {
    void loadMapped();
  }, [loadMapped]);

  useEffect(() => {
    if (!restaurantId) return undefined;
    const t = setTimeout(() => {
      void searchNearby();
    }, 350);
    return () => clearTimeout(t);
  }, [restaurantId, name, radiusKm, searchNearby]);

  const handleMap = async (organizationId) => {
    if (!restaurantId || !userId || !organizationId) return;
    setActionId(String(organizationId));
    setMessage('');
    try {
      const data = await mapRestaurantJreatsSchool(restaurantId, organizationId, userId);
      const schools = Array.isArray(data?.schools)
        ? data.schools
        : await fetchRestaurantJreatsSchools(restaurantId);
      setMapped(schools);
      setNearby((prev) =>
        prev.map((row) =>
          orgIdOf(row) === String(organizationId) ? { ...row, mapped: true } : row,
        ),
      );
      setMessage('School mapped for Jr Eats.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to map school.');
    } finally {
      setActionId(null);
    }
  };

  const handleUnmap = async (organizationId) => {
    if (!restaurantId || !organizationId) return;
    setActionId(String(organizationId));
    setMessage('');
    try {
      const data = await unmapRestaurantJreatsSchool(restaurantId, organizationId);
      const schools = Array.isArray(data?.schools)
        ? data.schools
        : await fetchRestaurantJreatsSchools(restaurantId);
      setMapped(schools);
      setNearby((prev) =>
        prev.map((row) =>
          orgIdOf(row) === String(organizationId) ? { ...row, mapped: false } : row,
        ),
      );
      setMessage('School unmapped.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to unmap school.');
    } finally {
      setActionId(null);
    }
  };

  const combinedRows = useMemo(() => {
    const byId = new Map();
    for (const row of mapped) {
      const id = orgIdOf(row);
      if (!id) continue;
      byId.set(id, { ...row, mapped: true });
    }
    for (const row of nearby) {
      const id = orgIdOf(row);
      if (!id) continue;
      const existing = byId.get(id);
      byId.set(id, {
        ...(existing || {}),
        ...row,
        mapped: existing ? true : Boolean(row.mapped || mappedIds.has(id)),
      });
    }
    return [...byId.values()].sort((a, b) => {
      if (Boolean(a.isPrimary) !== Boolean(b.isPrimary)) {
        return a.isPrimary ? -1 : 1;
      }
      const da = a.distanceKm == null ? Number.POSITIVE_INFINITY : Number(a.distanceKm);
      const db = b.distanceKm == null ? Number.POSITIVE_INFINITY : Number(b.distanceKm);
      if (da !== db) return da - db;
      return orgNameOf(a).localeCompare(orgNameOf(b));
    });
  }, [mapped, mappedIds, nearby]);

  return (
    <div className="age-pricing-editor meal-delivery-editor">
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">Map Schools (Jr Eats)</label>
        <p className="age-pricing-editor__hint">
          Link schools this restaurant serves for lunchbox pickup. Mapping uses this restaurant&apos;s
          pickup hub (created automatically if needed). Same schools appear under Eats admin → Pickup
          Hubs.
        </p>
      </div>

      <div className="get-restaurant-form-row flex" style={{ gap: 8, marginBottom: 10 }}>
        <div className="get-restaurant-form-group">
          <label>Search school name</label>
          <input
            type="text"
            value={name}
            disabled={disabled || searching}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. DPS Hyderabad"
          />
        </div>
        <div className="get-restaurant-form-group">
          <label>Radius (km)</label>
          <input
            type="number"
            min="1"
            max="50"
            value={radiusKm}
            disabled={disabled || searching}
            onChange={(e) => setRadiusKm(e.target.value)}
          />
        </div>
        <div className="get-restaurant-form-group" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={disabled || searching || !restaurantId}
            onClick={() => void searchNearby()}
          >
            {searching ? 'Searching…' : 'Search nearby'}
          </button>
        </div>
      </div>

      {message ? <p className="age-pricing-editor__hint">{message}</p> : null}
      {loading ? <p className="age-pricing-editor__hint">Loading mapped schools…</p> : null}

      <div className="age-pricing-editor__table-wrap">
        <table className="age-pricing-editor__table">
          <thead>
            <tr>
              <th>School</th>
              <th>Type</th>
              <th>Distance</th>
              <th>Address</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {combinedRows.length === 0 ? (
              <tr>
                <td colSpan={5}>No schools yet. Ensure the restaurant has an address with coordinates.</td>
              </tr>
            ) : (
              combinedRows.map((row) => {
                const id = orgIdOf(row);
                const mappedRow = Boolean(row.mapped || mappedIds.has(id));
                const busy = actionId === id;
                return (
                  <tr key={id}>
                    <td>
                      <strong>{orgNameOf(row)}</strong>
                      <div className="age-pricing-editor__hint">
                        ID {id}
                        {row.isPrimary ? ' · Primary' : ''}
                      </div>
                    </td>
                    <td>{row.organizationType || '—'}</td>
                    <td>
                      {row.distanceKm != null && Number.isFinite(Number(row.distanceKm))
                        ? `${Number(row.distanceKm).toFixed(1)} km`
                        : '—'}
                    </td>
                    <td>{row.addressLine || '—'}</td>
                    <td>
                      {mappedRow ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={disabled || busy}
                          onClick={() => void handleUnmap(id)}
                        >
                          {busy ? '…' : 'Remove'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={disabled || busy || !userId}
                          onClick={() => void handleMap(id)}
                        >
                          {busy ? '…' : 'Add'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
